import {useCallback, useState} from 'react';

import type {User} from 'nutshell-core/users/users-types';

import type {Tag} from 'shells/tags-editor/tags-editor';

import type {WidgetFragment} from 'nutshell-graphql-types';

import {CreateInfo} from '../../../shared/summary-widget/create-info/create-info';
import {PeepLastContacted} from '../../../shared/summary-widget/peep-last-contacted';
import {PeepTerritory} from '../../../shared/summary-widget/peep-territory';
import {PeepTagsEditor} from '../../../shared/tags-editor/peep-tags-editor';

import {SummaryWidgetContent} from '../../../../core-entity/widgets/summary-widget/summary-widget-content';

import {useGetAccountLastContactedTime} from '../../graphql/hooks/use-get-account-last-contacted-time';

import {AccountEmployeeCountField} from './account-employee-count-field';
import {AccountIndustryField} from './account-industry-field';
import {AccountRevenueField} from './account-revenue-field';
import {AccountTypeField} from './account-type-field';
import {useGetAccountSummary} from './graphql/hooks/use-get-account-summary';
import {useUpdateAccountSummary} from './graphql/hooks/use-update-account-summary';

interface Props {
    accountId: string;
    widget: WidgetFragment;
    user: User;
}
export function AccountSummaryWidget(props: Props) {
    const [error, setError] = useState<string | null | undefined>(null);
    const {accountSummary, isLoading} = useGetAccountSummary(props.accountId);
    const {accountLastContact, isLoading: isLoadingAccountLastContact} =
        useGetAccountLastContactedTime(props.accountId);
    const {updateAccount} = useUpdateAccountSummary();

    const handleUpdateTags = useCallback(
        (tags: Tag[]) => {
            if (!accountSummary) return Promise.resolve();

            return updateAccount(
                {
                    accountId: accountSummary.id,
                    tags: tags.map((tag) => ({name: tag.name, tagType: 'ACCOUNT' as const})),
                },
                accountSummary,
                undefined,
                tags
            );
        },
        [updateAccount, accountSummary]
    );

    const handleUpdateAccountType = useCallback(
        (accountTypeId: string, accountTypeName: string) => {
            if (!accountSummary) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: accountSummary.id,
                    accountTypeId,
                },
                accountSummary,
                {accountTypeName}
            ).catch((error) => {
                setError(error.message);
            });
        },
        [updateAccount, accountSummary]
    );

    const handleUpdateAccountIndustry = useCallback(
        (industryId: string | null, industryName: string | null) => {
            if (!accountSummary) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: accountSummary.id,
                    industryId,
                },
                accountSummary,
                {industryName}
            ).catch((error) => {
                setError(error.message);
            });
        },
        [updateAccount, accountSummary]
    );

    const handleUpdateAccountTerritory = useCallback(
        (territoryId: string | null, territoryName: string | null) => {
            if (!accountSummary) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: accountSummary.id,
                    territoryId,
                },
                accountSummary,
                {territoryName}
            ).catch((error) => {
                setError(error.message);
            });
        },
        [updateAccount, accountSummary]
    );

    const handleUpdateAccountEmployeeCount = useCallback(
        (employeeCount: number) => {
            if (!accountSummary) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: accountSummary.id,
                    employeeCount,
                },
                accountSummary
            ).catch((error) => {
                setError(error.message);
            });
        },
        [updateAccount, accountSummary]
    );

    const handleUpdateAccountRevenue = useCallback(
        (revenue: number) => {
            if (!accountSummary) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: accountSummary.id,
                    revenue,
                },
                accountSummary
            ).catch((error) => {
                setError(error.message);
            });
        },
        [updateAccount, accountSummary]
    );

    return (
        <SummaryWidgetContent
            widget={props.widget}
            fields={
                <>
                    <AccountTypeField
                        accountTypeId={accountSummary?.accountType?.id ?? null}
                        onUpdateAccountType={handleUpdateAccountType}
                        canAccessSetup={props.user.permissions.canAccessSetup}
                    />
                    <AccountIndustryField
                        industryId={accountSummary?.industry?.id ?? null}
                        onUpdateAccountIndustry={handleUpdateAccountIndustry}
                        canAccessSetup={props.user.permissions.canAccessSetup}
                    />
                    <PeepTerritory
                        territoryId={accountSummary?.territory?.id ?? null}
                        onUpdatePeepTerritory={handleUpdateAccountTerritory}
                        canAccessSetup={props.user.permissions.canAccessSetup}
                    />
                    <AccountEmployeeCountField
                        employeeCount={accountSummary?.employeeCount ?? null}
                        onUpdateAccountEmployeeCount={handleUpdateAccountEmployeeCount}
                    />
                    <AccountRevenueField
                        revenue={accountSummary?.revenue?.currencyValue.amount}
                        onUpdateAccountRevenue={handleUpdateAccountRevenue}
                    />
                    <PeepLastContacted
                        entityType='account'
                        lastContactedTime={accountLastContact?.lastContactedTime ?? null}
                        myLastContactedTime={accountLastContact?.myLastContactedTime ?? null}
                        isLoadingPeepLastContact={isLoading}
                    />
                    <CreateInfo
                        creatorUser={accountSummary?.creatorUser ?? null}
                        createdTime={accountSummary?.createdTime ?? null}
                    />
                </>
            }
            isLoading={isLoading || isLoadingAccountLastContact}
            tags={
                accountSummary ? (
                    <PeepTagsEditor
                        entityId={props.accountId}
                        entityType='ACCOUNT'
                        user={props.user}
                        tagMaps={accountSummary.tagMaps?.edges}
                        onUpdateTags={handleUpdateTags}
                        maxItems={5}
                    />
                ) : null
            }
            error={error}
            setError={setError}
        />
    );
}
